import "./simple-pdf-tool.css";
import { openBrowserPdf, pdfErrorMessage, renderPageIntoCanvas } from "./pdf-browser-core.js";
import { createOperationResult } from "./operation-result.js";
import { formatFileSize } from "./pdf-transform-core.js";
import { normalizeResizeSettings, resizedPdfName } from "./resize-pdf-core.js";

const root = document.querySelector("[data-resize-pdf]");

if (root) {
  const ids = [
    "resizeStatus", "resizeUploadPanel", "resizeDropZone", "chooseResizePdf", "resizeFileInput",
    "resizeWorkspace", "resizeFileName", "resizeFileDetails", "replaceResizePdf", "resizePreview",
    "resizePageSize", "resizeOrientation", "resizeMargin", "resizePdf", "resizeProgress"
  ];
  const elements = Object.fromEntries(ids.map(id => [id, document.getElementById(id)]));
  const state = { file: null, pageCount: 0, busy: false, worker: null, document: null };
  const result = createOperationResult(root, elements.resizeWorkspace);

  function status(message = "", kind = "error") {
    elements.resizeStatus.textContent = message;
    elements.resizeStatus.dataset.kind = kind;
    elements.resizeStatus.hidden = !message;
  }

  function controls() {
    for (const element of [elements.chooseResizePdf, elements.replaceResizePdf, elements.resizePageSize, elements.resizeOrientation, elements.resizeMargin])
      element.disabled = state.busy;
    elements.resizePdf.disabled = state.busy || !state.file;
    elements.resizeProgress.hidden = !state.busy;
    root.setAttribute("aria-busy", String(state.busy));
  }

  async function releaseDocument() {
    const document = state.document;
    state.document = null;
    if (document) await document.destroy();
  }

  async function loadFile(file) {
    if (!file || state.busy) return;
    state.busy = true;
    status("Opening PDF…", "progress");
    controls();
    let loaded;
    try {
      await releaseDocument();
      loaded = await openBrowserPdf(file);
      state.file = file;
      state.pageCount = loaded.pageCount;
      state.document = loaded.document;
      const firstPage = await loaded.document.getPage(1);
      try {
        const natural = firstPage.getViewport({ scale: 1 });
        const scale = Math.min(1.1, 520 / Math.max(natural.width, natural.height));
        await renderPageIntoCanvas(firstPage, elements.resizePreview, { scale });
      } finally {
        firstPage.cleanup();
      }
      elements.resizeFileName.textContent = file.name || "document.pdf";
      elements.resizeFileDetails.textContent = `${formatFileSize(file.size)} · ${loaded.pageCount} ${loaded.pageCount === 1 ? "page" : "pages"}`;
      elements.resizeUploadPanel.hidden = true;
      elements.resizeWorkspace.hidden = false;
      status();
    } catch (error) {
      if (loaded?.document && loaded.document !== state.document) await loaded.document.destroy();
      status(pdfErrorMessage(error));
    } finally {
      state.busy = false;
      elements.resizeFileInput.value = "";
      controls();
    }
  }

  function runWorker() {
    return new Promise(async (resolve, reject) => {
      const worker = new Worker("/dist/resize-pdf-worker.js", { type: "module" });
      state.worker = worker;
      const id = crypto.randomUUID();
      const finish = callback => {
        worker.terminate();
        if (state.worker === worker) state.worker = null;
        callback();
      };
      worker.addEventListener("message", event => {
        if (event.data?.id !== id) return;
        event.data.ok ? finish(() => resolve(event.data)) : finish(() => reject(new Error(event.data.error)));
      });
      worker.addEventListener("error", () => finish(() => reject(new Error("The PDF resizer could not start."))), { once: true });
      try {
        const buffer = await state.file.arrayBuffer();
        const settings = normalizeResizeSettings({
          pageSize: elements.resizePageSize.value,
          orientation: elements.resizeOrientation.value,
          margin: elements.resizeMargin.value
        });
        worker.postMessage({ id, buffer, settings }, [buffer]);
      } catch (error) {
        finish(() => reject(error));
      }
    });
  }

  async function resize() {
    if (!state.file || state.busy) return;
    state.busy = true;
    status("Resizing PDF pages…", "progress");
    controls();
    try {
      const output = await runWorker();
      result.show({
        blob: new Blob([output.buffer], { type: "application/pdf" }),
        fileName: resizedPdfName(state.file.name),
        message: "Your PDF pages were resized successfully.",
        details: [`${output.pageCount} ${output.pageCount === 1 ? "page" : "pages"}`, `${elements.resizePageSize.selectedOptions[0].text} · ${elements.resizeOrientation.selectedOptions[0].text}`]
      });
    } catch (error) {
      status(error?.message || "The PDF could not be resized.");
    } finally {
      state.busy = false;
      controls();
    }
  }

  elements.chooseResizePdf.addEventListener("click", event => { event.stopPropagation(); elements.resizeFileInput.click(); });
  elements.replaceResizePdf.addEventListener("click", () => elements.resizeFileInput.click());
  elements.resizeFileInput.addEventListener("change", () => loadFile(elements.resizeFileInput.files?.[0]));
  elements.resizePdf.addEventListener("click", resize);
  elements.resizeDropZone.addEventListener("click", event => { if (!event.target.closest("button")) elements.resizeFileInput.click(); });
  elements.resizeDropZone.addEventListener("keydown", event => {
    if (event.key === "Enter" || event.key === " ") { event.preventDefault(); elements.resizeFileInput.click(); }
  });
  elements.resizeDropZone.addEventListener("dragover", event => { event.preventDefault(); elements.resizeDropZone.classList.add("is-dragging"); });
  elements.resizeDropZone.addEventListener("dragleave", () => elements.resizeDropZone.classList.remove("is-dragging"));
  elements.resizeDropZone.addEventListener("drop", event => {
    event.preventDefault();
    elements.resizeDropZone.classList.remove("is-dragging");
    loadFile(event.dataTransfer?.files?.[0]);
  });
  window.addEventListener("pagehide", () => {
    state.worker?.terminate();
    void releaseDocument();
    result.dispose();
  }, { once: true });
  controls();
}
